/**
 * Nutrix Wishlist — localStorage-based wishlist with slide-out drawer
 */
(function () {
  'use strict';

  const STORAGE_KEY = 'nutrix_wishlist';

  /* ===== Helpers ===== */
  function getWishlist() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch (e) {
      return [];
    }
  }

  function saveWishlist(list) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  }

  function isInWishlist(handle) {
    return getWishlist().includes(handle);
  }

  function addToWishlist(handle) {
    const list = getWishlist();
    if (!list.includes(handle)) {
      list.push(handle);
      saveWishlist(list);
    }
  }

  function removeFromWishlist(handle) {
    const list = getWishlist().filter(h => h !== handle);
    saveWishlist(list);
  }

  function toggleWishlist(handle) {
    if (isInWishlist(handle)) {
      removeFromWishlist(handle);
      return false;
    } else {
      addToWishlist(handle);
      return true;
    }
  }

  /* ===== Badge Count ===== */
  function updateBadge() {
    const count = getWishlist().length;
    document.querySelectorAll('.nx-wishlist-count').forEach(el => {
      if (count > 0) {
        el.textContent = count > 99 ? '99+' : count;
        el.style.display = '';
      } else {
        el.style.display = 'none';
      }
    });
    // Update drawer count too
    document.querySelectorAll('[data-wishlist-drawer-count]').forEach(el => {
      el.textContent = count > 0 ? `(${count})` : '';
    });
  }

  /* ===== Heart Button States ===== */
  function syncAllButtons() {
    document.querySelectorAll('[data-wishlist-handle]').forEach(btn => {
      const handle = btn.getAttribute('data-wishlist-handle');
      if (!handle) return;
      const active = isInWishlist(handle);
      btn.classList.toggle('is-wishlisted', active);
      btn.setAttribute('aria-label', active ? 'Remove from wishlist' : 'Add to wishlist');
      const path = btn.querySelector('svg path');
      if (path) {
        path.setAttribute('fill', active ? 'currentColor' : 'none');
      }
    });
  }

  /* ===== Drawer ===== */
  const drawer = document.querySelector('[data-wishlist-drawer]');
  const overlay = document.querySelector('[data-wishlist-overlay]');
  const drawerBody = document.querySelector('[data-wishlist-drawer-body]');
  const drawerEmpty = document.querySelector('[data-wishlist-drawer-empty]');
  const drawerFooter = document.querySelector('[data-wishlist-drawer-footer]');

  function openDrawer() {
    if (!drawer) return;
    document.body.style.overflow = 'hidden';
    drawer.classList.add('is-open');
    if (overlay) overlay.classList.add('is-open');
    renderDrawer();
  }

  function closeDrawer() {
    if (!drawer) return;
    document.body.style.overflow = '';
    drawer.classList.remove('is-open');
    if (overlay) overlay.classList.remove('is-open');
  }

  function renderDrawer() {
    if (!drawerBody) return;

    const list = getWishlist();

    if (list.length === 0) {
      drawerBody.innerHTML = '';
      drawerBody.style.display = 'none';
      if (drawerEmpty) drawerEmpty.style.display = '';
      if (drawerFooter) drawerFooter.style.display = 'none';
      return;
    }

    if (drawerEmpty) drawerEmpty.style.display = 'none';
    drawerBody.style.display = '';
    if (drawerFooter) drawerFooter.style.display = '';

    drawerBody.innerHTML = '<div class="nx-wl-drawer__loading">Loading...</div>';

    const fetches = list.map(handle =>
      fetch(`/products/${handle}.js`)
        .then(r => {
          if (!r.ok) throw new Error('not found');
          return r.json();
        })
        .catch(() => null)
    );

    Promise.all(fetches).then(products => {
      const valid = products.filter(Boolean);

      // Clean stale handles
      if (valid.length !== list.length) {
        const validHandles = valid.map(p => p.handle);
        saveWishlist(validHandles);
        updateBadge();
      }

      if (valid.length === 0) {
        drawerBody.innerHTML = '';
        drawerBody.style.display = 'none';
        if (drawerEmpty) drawerEmpty.style.display = '';
        if (drawerFooter) drawerFooter.style.display = 'none';
        return;
      }

      drawerBody.innerHTML = valid.map(product => {
        const img = product.featured_image || '';
        const url = `/products/${product.handle}`;

        // Prefer the 3-month variant price; fallback to last variant, then product price
        let displayPrice = product.price;
        let displayCompare = product.compare_at_price;
        let variantLabel = '';
        if (product.variants && product.variants.length > 1) {
          const threeMonth = product.variants.find(v =>
            (v.title || '').toLowerCase().includes('3 month')
          );
          if (threeMonth) {
            displayPrice = threeMonth.price;
            displayCompare = threeMonth.compare_at_price;
            variantLabel = threeMonth.title;
          }
        }
        const price = (displayPrice / 100).toFixed(2);
        const comparePrice = displayCompare ? (displayCompare / 100).toFixed(2) : null;

        return `
          <div class="nx-wl-item" data-wl-item="${product.handle}">
            <div class="nx-wl-item__img">
              <a href="${url}">
                ${img ? `<img src="${img}" alt="${product.title}" loading="lazy">` : ''}
              </a>
            </div>
            <div class="nx-wl-item__info">
              <a href="${url}" class="nx-wl-item__name">${product.title}</a>
              ${variantLabel ? `<span class="nx-wl-item__variant">${variantLabel}</span>` : ''}
              <p class="nx-wl-item__price">
                ${comparePrice ? `<span class="compare">${comparePrice} AED</span>` : ''}
                ${price} AED
              </p>
              <a href="${url}" class="nx-wl-item__view">View Product</a>
            </div>
            <div class="nx-wl-item__remove">
              <button data-wl-remove="${product.handle}" aria-label="Remove ${product.title}">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>
          </div>
        `;
      }).join('');

      // Wire remove buttons
      drawerBody.querySelectorAll('[data-wl-remove]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const handle = btn.getAttribute('data-wl-remove');
          removeFromWishlist(handle);

          const item = btn.closest('.nx-wl-item');
          if (item) {
            item.style.opacity = '0';
            item.style.transform = 'translateX(30px)';
            setTimeout(() => {
              item.remove();
              updateBadge();
              syncAllButtons();
              if (getWishlist().length === 0) {
                drawerBody.style.display = 'none';
                if (drawerEmpty) drawerEmpty.style.display = '';
                if (drawerFooter) drawerFooter.style.display = 'none';
              }
            }, 300);
          }

          showToast('Removed from wishlist');
        });
      });
    });
  }

  /* ===== Product Card Click Handler ===== */
  function handleWishlistClick(e) {
    const btn = e.target.closest('[data-wishlist-handle]');
    if (!btn) return;

    e.preventDefault();
    e.stopPropagation();

    const handle = btn.getAttribute('data-wishlist-handle');
    if (!handle) return;

    const added = toggleWishlist(handle);

    btn.classList.toggle('is-wishlisted', added);
    btn.setAttribute('aria-label', added ? 'Remove from wishlist' : 'Add to wishlist');
    const path = btn.querySelector('svg path');
    if (path) {
      path.setAttribute('fill', added ? 'currentColor' : 'none');
    }

    // Pulse
    btn.classList.add('nx-wishlist-pulse');
    setTimeout(() => btn.classList.remove('nx-wishlist-pulse'), 350);

    syncAllButtons();
    updateBadge();

    showToast(added ? 'Added to wishlist ♥' : 'Removed from wishlist');
  }

  /* ===== Toast ===== */
  function showToast(message) {
    const existing = document.querySelector('.nx-wishlist-toast');
    if (existing) existing.remove();

    const toast = document.createElement('div');
    toast.className = 'nx-wishlist-toast';
    toast.textContent = message;
    document.body.appendChild(toast);

    requestAnimationFrame(() => {
      toast.classList.add('is-visible');
    });

    setTimeout(() => {
      toast.classList.remove('is-visible');
      setTimeout(() => toast.remove(), 300);
    }, 2000);
  }

  /* ===== Init ===== */
  function init() {
    // Product card wishlist buttons (global click delegation)
    document.addEventListener('click', handleWishlistClick);

    // Drawer toggle button in header
    document.querySelectorAll('[data-wishlist-toggle]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        openDrawer();
      });
    });

    // Close drawer
    document.querySelectorAll('[data-wishlist-close]').forEach(btn => {
      btn.addEventListener('click', closeDrawer);
    });

    // Close on overlay click
    if (overlay) {
      overlay.addEventListener('click', closeDrawer);
    }

    // Close on Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer && drawer.classList.contains('is-open')) {
        closeDrawer();
      }
    });

    // Initial sync
    syncAllButtons();
    updateBadge();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
